import React, { useState, useRef, useCallback } from 'react';
import { resolveAvatarUrl } from '../../../constants/assets';
import { Camera, X, Loader2, Check, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ImagePreviewModal from '../../UI/ImagePreviewModal';

interface AvatarUploadProps {
    currentAvatarUrl: string;
    onAvatarChange?: (file: File, previewUrl: string) => void;
    onUpload: (file: File) => Promise<string | null>;
    isUploading?: boolean;
}

// Helper function to create centered crop
function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    );
}

async function getCroppedAvatarFile(
    image: HTMLImageElement,
    crop: PixelCrop,
    scale: number = 1
): Promise<File | null> {
    const canvas = document.createElement('canvas');

    // scaleX/Y converts from displayed image coordinates to natural image coordinates
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Fixed output size for avatar (400x400 is good for profile)
    const outputSize = 400;

    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Fill with white background first (for images smaller than crop area)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outputSize, outputSize);

    ctx.imageSmoothingQuality = 'high';

    // Simple and direct calculation:
    // The crop box coordinates are in DISPLAYED image pixels
    // We need to convert them to NATURAL image pixels

    // When scale > 1 (zoomed in), we want to crop a SMALLER portion of the original
    // centered at the same point

    // First, find where the crop center is in the displayed image
    const cropCenterX = crop.x + crop.width / 2;
    const cropCenterY = crop.y + crop.height / 2;

    // Convert crop center to natural image coordinates
    const naturalCenterX = cropCenterX * scaleX;
    const naturalCenterY = cropCenterY * scaleY;

    // Calculate source size: when zoomed in, we take a smaller portion
    // crop.width/height is in display pixels
    // Divide by scale because zoom means we want less of the original image
    const sourceWidth = (crop.width * scaleX) / scale;
    const sourceHeight = (crop.height * scaleY) / scale;

    // Calculate source top-left from center
    const sourceX = naturalCenterX - sourceWidth / 2;
    const sourceY = naturalCenterY - sourceHeight / 2;

    ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        outputSize,
        outputSize
    );

    const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });

    if (!blob) return null;

    return new File([blob], `avatar_${Date.now()}.jpg`, { type: 'image/jpeg' });
}


const AvatarUpload: React.FC<AvatarUploadProps> = ({
    currentAvatarUrl,
    onAvatarChange,
    onUpload,
    isUploading = false
}) => {
    const [showModal, setShowModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    // Cropping states
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

    // Zoom state
    const [scale, setScale] = useState<number>(1);
    const MIN_SCALE = 0.5;
    const MAX_SCALE = 3;
    const SCALE_STEP = 0.1;

    const imgRef = useRef<HTMLImageElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const displayAvatar = resolveAvatarUrl(currentAvatarUrl);

    const validateFile = (file: File): string | null => {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const maxSize = 10 * 1024 * 1024; // 10MB

        if (!validTypes.includes(file.type)) {
            return '请上传 JPG、PNG、GIF 或 WebP 格式的图片';
        }

        if (file.size > maxSize) {
            return '图片大小不能超过 10MB';
        }

        return null;
    };

    const handleFileSelect = useCallback((file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        setScale(1); // Reset scale for new image

        // Read file and show cropper
        const reader = new FileReader();
        reader.onloadend = () => {
            setImageSrc(reader.result as string);
            setCrop(undefined);
            setCompletedCrop(undefined);
            onAvatarChange?.(file, reader.result as string);
        };
        reader.readAsDataURL(file);
    }, [onAvatarChange]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, 1)); // 1:1 aspect ratio for avatar
    }, []);

    const handleZoomIn = () => {
        setScale(prev => Math.min(MAX_SCALE, prev + SCALE_STEP));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(MIN_SCALE, prev - SCALE_STEP));
    };

    const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setScale(parseFloat(e.target.value));
    };

    const handleConfirmUpload = async () => {
        if (!imgRef.current || !completedCrop) {
            setError('请先裁剪图片');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const croppedFile = await getCroppedAvatarFile(
                imgRef.current,
                completedCrop,
                scale
            );

            if (!croppedFile) {
                setError('图片处理失败，请重试');
                return;
            }

            const uploadedUrl = await onUpload(croppedFile);
            if (uploadedUrl) {
                setShowModal(false);
                handleCancel();
            } else {
                setError('上传失败，请重试');
            }
        } catch (err) {
            setError('上传失败，请重试');
        } finally {
            setUploading(false);
        }
    };

    const handleCancel = () => {
        setImageSrc(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setError(null);
        setScale(1);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        handleCancel();
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleReselect = () => {
        setImageSrc(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setScale(1);
        openFileDialog();
    };

    return (
        <div className="flex items-center gap-6 pb-6 border-b border-gray-100">
            {/* Avatar Display */}
            <div className="relative group">
                <button
                    type="button"
                    onClick={() => setShowPreviewModal(true)}
                    className="relative block"
                    aria-label="查看头像大图"
                >
                    <img
                        src={displayAvatar}
                        alt="头像"
                        className="w-20 h-20 rounded-full object-cover border-2 border-gray-100 transition-all group-hover:border-gray-300 group-hover:scale-[1.02]"
                    />
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                        查看大图
                    </span>
                </button>

                {/* Upload indicator */}
                {(uploading || isUploading) && (
                    <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex-1">
                <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                    更换头像
                </button>
                <p className="text-xs text-gray-500 mt-2">支持 JPG, PNG, GIF, WebP, 最大 10MB，点击头像可查看大图</p>
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleInputChange}
                className="hidden"
            />

            {/* Upload & Crop Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleCloseModal}
                    />

                    {/* Modal Content */}
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
                        {/* Close Button */}
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>

                        <h3 className="text-xl font-bold text-gray-900 mb-6">更换头像</h3>

                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {!imageSrc ? (
                            /* Drop Zone - Show when no image selected */
                            <div
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={openFileDialog}
                                className={`
                                    border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                                    ${dragActive
                                        ? 'border-black bg-gray-50'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }
                                `}
                            >
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Camera className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-700 font-medium mb-2">
                                    拖拽图片到这里，或点击选择
                                </p>
                                <p className="text-sm text-gray-500">
                                    支持 JPG、PNG、GIF、WebP，最大 10MB
                                </p>
                            </div>
                        ) : (
                            /* Cropper - Show when image selected */
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-2">
                                    拖动调整裁剪区域，使用缩放控制图片大小
                                </p>

                                {/* Crop Area */}
                                <div className="flex justify-center bg-gray-100 rounded-xl p-4 overflow-hidden">
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(c) => setCrop(c)}
                                        onComplete={(c) => setCompletedCrop(c)}
                                        aspect={1}
                                        circularCrop
                                        className="max-h-[350px]"
                                    >
                                        <img
                                            ref={imgRef}
                                            src={imageSrc}
                                            alt="待裁剪图片"
                                            onLoad={onImageLoad}
                                            style={{
                                                maxHeight: '350px',
                                                maxWidth: '100%',
                                                transform: `scale(${scale})`,
                                                transformOrigin: 'center center'
                                            }}
                                        />
                                    </ReactCrop>
                                </div>

                                {/* Zoom Control */}
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <button
                                        type="button"
                                        onClick={handleZoomOut}
                                        disabled={scale <= MIN_SCALE}
                                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ZoomOut className="w-5 h-5 text-gray-600" />
                                    </button>

                                    <div className="flex-1 flex items-center gap-3">
                                        <input
                                            type="range"
                                            min={MIN_SCALE}
                                            max={MAX_SCALE}
                                            step={SCALE_STEP}
                                            value={scale}
                                            onChange={handleScaleChange}
                                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black"
                                        />
                                        <span className="text-sm text-gray-500 w-12 text-right">
                                            {Math.round(scale * 100)}%
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleZoomIn}
                                        disabled={scale >= MAX_SCALE}
                                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ZoomIn className="w-5 h-5 text-gray-600" />
                                    </button>
                                </div>

                                {/* Preview */}
                                {completedCrop && (
                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                        <div className="flex-shrink-0">
                                            <p className="text-xs text-gray-500 mb-2">预览效果</p>
                                            <div className="flex gap-3">
                                                {/* Large preview */}
                                                <div
                                                    className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-md bg-white"
                                                    style={{
                                                        backgroundImage: `url(${imageSrc})`,
                                                        backgroundSize: `${(imgRef.current?.width || 0) * scale / (completedCrop.width / 64)}px`,
                                                        backgroundPosition: `-${(completedCrop.x / (completedCrop.width / 64))}px -${(completedCrop.y / (completedCrop.width / 64))}px`
                                                    }}
                                                />
                                                {/* Small preview */}
                                                <div
                                                    className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-md bg-white"
                                                    style={{
                                                        backgroundImage: `url(${imageSrc})`,
                                                        backgroundSize: `${(imgRef.current?.width || 0) * scale / (completedCrop.width / 40)}px`,
                                                        backgroundPosition: `-${(completedCrop.x / (completedCrop.width / 40))}px -${(completedCrop.y / (completedCrop.width / 40))}px`
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1 text-sm text-gray-600">
                                            <p>头像将以圆形展示在个人主页</p>
                                            <p className="text-xs text-gray-400 mt-1">小于区域的图片会自动填充白色背景</p>
                                        </div>
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={handleReselect}
                                        className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        重新选择
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmUpload}
                                        disabled={uploading || !completedCrop}
                                        className="flex-1 px-4 py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                上传中...
                                            </>
                                        ) : (
                                            <>
                                                <Check className="w-4 h-4" />
                                                确认更换
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Current Avatar Preview - Only show when no image selected */}
                        {!imageSrc && (
                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <p className="text-sm text-gray-500 mb-3">当前头像</p>
                                <div className="flex items-center gap-4">
                                <img
                                    src={displayAvatar}
                                    alt="当前头像"
                                    className="w-16 h-16 rounded-full object-cover border border-gray-200"
                                />
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-600">
                                            选择新图片后可裁剪、缩放并预览效果
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ImagePreviewModal
                isOpen={showPreviewModal}
                imageUrl={displayAvatar}
                alt="头像大图"
                title="头像预览"
                onClose={() => setShowPreviewModal(false)}
            />
        </div>
    );
};

export default AvatarUpload;
